import React from 'react';
import styled from 'styled-components';
import CssSpinner from '../css-spinner';
import {
  gray1, gray10, gray8, blue1, blue7, darkBlue3, darkBlue1, darkBlue6, surface, darkBlue4,
} from '../../colours';
import { uiTextMedium } from '../../typography';

interface StyleProps {
  'data-icon-only': boolean | undefined
  'data-left-icon': boolean | undefined
  'data-right-icon': boolean | undefined
  'data-outline': boolean | undefined
  'data-text': boolean | undefined
  'data-custom-width': string | undefined
}

export const ButtonStyle = styled.button`
  ${uiTextMedium};

  align-items: center;
  display: flex;
  background: ${(props: StyleProps) => (props['data-outline'] ? gray1 : gray10)};
  ${(props: StyleProps) => (props['data-text'] && 'background: transparent')};
  border: ${(props: StyleProps) => (props['data-outline'] ? `2px solid ${darkBlue1}` : 'none')};
  ${(props: StyleProps) => (props['data-text'] && 'border: none')}
  border-radius: 8px;
  color: ${(props: StyleProps) => (props['data-outline'] ? surface : gray1)};
  ${(props: StyleProps) => (props['data-text'] && `color: ${darkBlue4};`)}
  cursor: pointer;
  height: 32px;
  width: ${(props: StyleProps) => (props['data-custom-width'])};
  justify-content: center;
  padding: ${(props: StyleProps) => (props['data-icon-only'] ? '4px' : '4px 16px')};
  ${(props: StyleProps) => (props['data-text'] && 'padding: 4px;')}

  &:hover {
    background: ${gray8};
    ${(props: StyleProps) => (props['data-text'] && 'background: transparent;')}
    color: ${gray1};
    ${(props: StyleProps) => (props['data-text'] && `color: ${surface}`)};
    border-color: ${(props: StyleProps) => (props['data-outline'] && 'transparent')};

    path {
      fill: ${gray1};
    }
  }

  &:disabled {
    background: rgba(9, 30, 66, 0.04);
    color: ${darkBlue3};
    cursor: unset;

    path {
      fill: ${darkBlue3};
    }
  }

  &:not(:disabled) {
    &:focus {
      background: ${darkBlue6};
      color: ${gray1};

      path {
        fill: ${gray1};
      }
    }

    &:active {
      background: ${blue1};
      color: ${blue7};

      path {
        fill: ${blue7};
      }
    }
  }

  svg {
    margin-right: ${(props: StyleProps) => (props['data-left-icon'] ? '4px' : 0)};
    margin-left: ${(props: StyleProps) => (props['data-right-icon'] ? '4px' : 0)};
  }
`;

export interface ButtonSmallProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined
  isDisabled?: boolean,
  text: string,
  type?: 'submit' | 'reset' | 'button',
  hasLeftIcon?: boolean,
  hasRightIcon?: boolean,
  Icon?: any,
  loading?: boolean,
  isOutline?: boolean,
  isText?: boolean,
  customWidth?: string,
}

const ButtonSmall = ({
  onClick, isDisabled, text, type, Icon, hasLeftIcon,
  hasRightIcon, loading, isOutline, isText, customWidth,
}: ButtonSmallProps) => (
  <ButtonStyle
    data-icon-only={(!hasRightIcon && !hasLeftIcon && Icon !== undefined) || loading}
    data-left-icon={hasLeftIcon}
    data-right-icon={hasRightIcon}
    data-outline={isOutline}
    data-text={isText}
    onClick={onClick}
    disabled={isDisabled || loading}
    type={type}
    data-custom-width={customWidth}
  >
    {loading ? (
      <CssSpinner color={gray8} />
    ) : (
      <>
        {hasLeftIcon && <Icon />}
        {text}
        {!hasRightIcon && !hasLeftIcon && Icon !== undefined && <Icon />}
        {hasRightIcon && <Icon />}
      </>
    )}
  </ButtonStyle>
);

ButtonSmall.defaultProps = {
  onClick: undefined,
  type: 'button',
  isDisabled: false,
  Icon: undefined,
  hasLeftIcon: false,
  hasRightIcon: false,
  loading: false,
  isOutline: false,
  isText: false,
  customWidth: '',
};

export default ButtonSmall;
